/**
 * TeamStrength Hub - Invitation Signup Page
 * Users land here when clicking an invitation link
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { invitationApi, authApi, orgApi } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { Users, Mail, Lock, User, ArrowRight, Building2, AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';

const InviteSignupPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading: authLoading, checkAuth } = useAuth();
  
  const [inviteInfo, setInviteInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [joining, setJoining] = useState(false);

  useEffect(() => {
    const fetchInviteInfo = async () => {
      try {
        const response = await invitationApi.getInfo(token);
        setInviteInfo(response.data);
      } catch (error) {
        setInviteInfo({ valid: false, message: 'Invalid invitation link' });
      } finally {
        setLoading(false);
      }
    };
    
    fetchInviteInfo();
  }, [token]);

  // Handle already logged-in user joining organization
  const handleJoinAsExistingUser = async () => {
    setJoining(true);
    try {
      // Use the accept invitation endpoint
      const response = await invitationApi.accept(token);
      
      if (response.data.already_member) {
        toast.info('You are already a member of this organization');
        navigate('/dashboard');
        return;
      }
      
      await checkAuth(); // Refresh user data
      toast.success(`Welcome to ${inviteInfo.org_name}!`);
      navigate('/onboarding', { state: { orgId: inviteInfo.org_id } });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join organization');
    } finally {
      setJoining(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await authApi.signup({
        name,
        email,
        password,
        invitation_token: token
      });
      
      const { access_token } = response.data;
      localStorage.setItem('session_token', access_token);
      
      // Refresh auth state before navigating
      await checkAuth();
      
      toast.success(`Welcome to ${inviteInfo.org_name}!`);
      
      // Redirect to profile setup
      navigate('/onboarding', { state: { orgId: inviteInfo.org_id } });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Signup failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading || authLoading) {
    return (
      <div className="auth-page">
        <div className="w-12 h-12 border-4 border-violet-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!inviteInfo?.valid) {
    return (
      <div className="auth-page">
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-8 pb-8">
            <AlertTriangle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2" style={{ fontFamily: 'Manrope' }}>
              Invalid Invitation
            </h2>
            <p className="text-slate-500 dark:text-slate-400 mb-6">
              {inviteInfo?.message || 'This invitation link is invalid or has expired.'}
            </p>
            <Link to="/login">
              <Button variant="outline">
                Go to Login
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // User is already logged in - show join option
  if (isAuthenticated && user) {
    return (
      <div className="auth-page">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8 animate-fade-in">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-brand-gradient mb-4">
              <Users className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Manrope' }}>
              TeamStrength Hub
            </h1>
          </div>

          {/* Invitation Info */}
          <Card className="mb-6 animate-fade-in bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-emerald-600 dark:text-emerald-400">You've been invited to join</p>
                  <p className="font-bold text-emerald-700 dark:text-emerald-300" style={{ fontFamily: 'Manrope' }}>
                    {inviteInfo.org_name}
                  </p>
                  <p className="text-xs text-emerald-600 dark:text-emerald-500 capitalize">
                    as {inviteInfo.role.replace('_', ' ')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="animate-fade-in stagger-1 shadow-xl border-0">
            <CardHeader className="text-center">
              <CardTitle className="text-xl" style={{ fontFamily: 'Manrope' }}>
                Join as {user.name}?
              </CardTitle>
              <CardDescription>
                You're currently logged in as {user.email}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button 
                className="w-full bg-violet-600 hover:bg-violet-700"
                onClick={handleJoinAsExistingUser}
                disabled={joining}
                data-testid="join-org-btn"
              >
                {joining ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Join {inviteInfo.org_name}
                  </>
                )}
              </Button>

              <div className="relative">
                <Separator />
                <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white dark:bg-slate-900 px-2 text-xs text-slate-500">
                  OR
                </span>
              </div>

              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  localStorage.removeItem('session_token');
                  window.location.reload();
                }}
              >
                Use a different account
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // User is not logged in - show signup form
  return (
    <div className="auth-page">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8 animate-fade-in">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-brand-gradient mb-4">
            <Users className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Manrope' }}>
            TeamStrength Hub
          </h1>
        </div>

        {/* Invitation Info */}
        <Card className="mb-6 animate-fade-in bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                <Building2 className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-emerald-600 dark:text-emerald-400">You've been invited to join</p>
                <p className="font-bold text-emerald-700 dark:text-emerald-300" style={{ fontFamily: 'Manrope' }}>
                  {inviteInfo.org_name}
                </p>
                <p className="text-xs text-emerald-600 dark:text-emerald-500 capitalize">
                  as {inviteInfo.role.replace('_', ' ')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="animate-fade-in stagger-1 shadow-xl border-0">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-xl" style={{ fontFamily: 'Manrope' }}>Create your account</CardTitle>
            <CardDescription>
              Enter your details to join the organization
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Full Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="name"
                    type="text"
                    placeholder="John Doe"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="pl-10"
                    required
                    data-testid="invite-name-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="name@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    required
                    data-testid="invite-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10"
                    required
                    data-testid="invite-password-input"
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-violet-600 hover:bg-violet-700"
                disabled={isSubmitting}
                data-testid="invite-submit-btn"
              >
                {isSubmitting ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    Join Organization
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </form>

            <p className="text-center text-sm text-slate-500 mt-6">
              Already have an account?{' '}
              <Link to="/login" className="text-violet-600 hover:text-violet-700 font-medium">
                Sign in
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default InviteSignupPage;
